
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, ExternalLink, Clock, Database, Info } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { WorkflowRun } from '@/hooks/useWorkflowExecution';
import { formatDateTime } from '@/lib/format';

interface WorkflowDashboardTabProps {
  run: WorkflowRun;
}

export const WorkflowDashboardTab = ({ run }: WorkflowDashboardTabProps) => {
  const navigate = useNavigate();
  const stepResults = run.step_results as Record<string, any> | null;
  const dashboardData = stepResults?.dashboard_generation;

  if (!dashboardData) {
    return (
      <div className="py-12 text-center">
        <LayoutDashboard className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground">No dashboard was generated for this run.</p>
      </div>
    );
  }

  const widgets = dashboardData.widgets || [];

  return (
    <div className="space-y-6">
      {/* Traceability Info */}
      <Card className="p-4 bg-muted/50">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-primary mt-0.5" />
          <div className="space-y-1 text-sm">
            <p className="font-medium">Dashboard Traceability</p>
            <div className="flex flex-wrap gap-3 text-muted-foreground">
              <span className="flex items-center gap-1">
                <Database className="h-3 w-3" />
                Data Version: v{run.version}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Generated: {run.completed_at ? formatDateTime(run.completed_at) : 'In progress'}
              </span>
              <span>
                Source: {run.input_source === 'crm' ? 'CRM Integration' : 'Replayed data'}
              </span>
            </div>
            {run.status === 'rolled_back' && (
              <p className="text-orange-600 font-medium mt-1">
                ⚠ This workflow version has been rolled back. Dashboard data may no longer be current.
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Widgets Preview */}
      <div>
        <h4 className="font-medium mb-3">Generated Dashboard Widgets</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {widgets.map((widget: string, i: number) => (
            <Card key={i} className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <LayoutDashboard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">{widget}</p>
                <p className="text-xs text-muted-foreground">Auto-generated from workflow v{run.version}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Button
        onClick={() =>
          navigate(
            `/dashboards?sourceType=workflow&sourceId=${run.id}&sourceName=${encodeURIComponent(`Workflow run v${run.version}`)}`,
          )
        }
        className="flex items-center gap-2"
      >
        <ExternalLink className="h-4 w-4" />
        Open this run's dashboard
      </Button>
    </div>
  );
};
